import Vue from 'vue'
import * as log from 'loglevel'

import VueRouter from 'vue-router'
import VueWait from 'vue-wait'
import Vuex, { Store } from 'vuex'

import { Injector } from '@/config/dependency-injection'
// Add class component hooks for router specific hooks
import './config/class-component-hooks'
// Import the font-awesome icons we use (done like this so we can )
import './config/font-awesome'

import { DialogService } from '@/shared/dialog/dialog-service'
import { sync } from 'vuex-router-sync'
import { i18n } from '@/config/i18n'
import App from '@/App.vue'
import { NotificationService } from '@/shared/services/notification-service'
import { routes } from '@/router'
import { AppToken, RouterToken } from '@/config/injection-tokens'
import { FunctionQueue } from '@/commons/function-queue'
import { RouteHelper } from '@/commons/route-helper'
import { redirectGuard } from '@/commons/redirect-guard'
import { authenticationGuard } from '@/commons/authentication-guard'
import VueGtag from 'vue-gtag'
import { environmentConfiguration } from '@/config/environments'
import { noop } from '@/shared/utils/noop'
import { AuthenticationService } from '@/authentication/authentication-service'
import { applyVueExtensions } from '@/extensions'
import { routeSeoGuard } from '@/commons/route-seo-guard'

interface Dependencies {
  router: VueRouter
  store: Store<unknown>
}

export const onBootstrap = new FunctionQueue<Dependencies>()

/**
 * Bootstrap the application
 */
export async function bootstrap(): Promise<Vue> {
  const store = setupStore()
  const router = setupRouter()

  setupDynamicallyRegistered({
    router,
    store,
  })

  await setupAuthentication()

  setupRouterGuards(router)

  // Add noop function to vue
  Vue.prototype.$noop = noop

  // Add global methods to vue
  extendVue()

  const app = createVueApplication(router, store)

  Injector.bindConstant(AppToken, app)

  // Disable productionTip in console
  Vue.config.productionTip = false

  // Sync the router state to the store
  sync(store, router)

  // Create analytics
  setupAnalytics(router)

  setupFilters()
  setupLogger()
  setupServices(app)

  return app
}

function createVueApplication(router: VueRouter, store: Store<any>) {
  // Create application
  return new Vue({
    router,
    store,
    i18n,
    wait: new VueWait({
      useVuex: true,
    }),

    render: h => h(App),
  })
}

function extendVue() {
  applyVueExtensions()
}

function setupAnalytics(router: VueRouter) {
  const config = environmentConfiguration()

  if (config.analytics.enabled === true) {
    Vue.use(
      VueGtag,
      {
        bootstrap: false,
        config: {
          id: config.analytics.id,
        },
      },
      router
    )
  }
}

async function setupAuthentication() {
  const authenticationService = Injector.get(AuthenticationService)

  return authenticationService.initialize()
}

function setupDynamicallyRegistered(dependencies: Dependencies) {
  onBootstrap.call(dependencies)
}

/**
 * Install globally available filters
 */
function setupFilters() {
  //
}

/**
 * Set the default configuration for the logger
 */
function setupLogger() {
  log.setDefaultLevel(log.levels.DEBUG)
}

/**
 * Create the router and make it available to the DI
 */
function setupRouter(): VueRouter {
  const router = new VueRouter({
    routes,
    linkExactActiveClass: 'active',
    mode: 'history',
    scrollBehavior: RouteHelper.scrollBehavior,
  })

  Injector.bindConstant(RouterToken, router)

  return router
}

/**
 * Bind the routing guards to the router
 * @param router
 */
function setupRouterGuards(router: VueRouter): void {
  router.beforeEach(authenticationGuard)
  router.beforeEach(redirectGuard)
  router.beforeEach(routeSeoGuard)
}

/**
 * Initialise Services that need the instantiated Vue application and bind them with the dependency injection library
 * @param app
 */
function setupServices(app: Vue) {
  Injector.bindLazy(DialogService, () => new DialogService(app))
  Injector.bindLazy(NotificationService, () => new NotificationService(app))
}

/**
 * Setup the store and bind it with the dependency injection library
 */
function setupStore(): Store<unknown> {
  Vue.use(Vuex)

  const debug = process.env.NODE_ENV !== 'production'

  const store = new Vuex.Store({
    strict: debug,
  })

  Injector.bindLazy(Store, () => store)

  return store
}
